import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type { UseQueryOptions } from '@tanstack/react-query';
import {
  useMutation,
  type UseMutationResult,
  useQueryClient,
  type UseQueryResult,
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  type InfiniteData,
} from '@tanstack/react-query';
import type { ReceiptFull } from '@pflegenavi/shared/api';

import { createContext, useCallback } from 'react';
import { invalidateAllResidentBalance } from '../reporting/queryKeys';
import {
  RESIDENTS_KEY,
  invalidatePendingReceiptCount,
} from '../resident/queryKeys';
import { RESIDENTS_PHOENIX_KEY } from '../resident-phoenix/queryKeys';
import {
  getReceiptKey,
  SUBMIT_RECEIPTS_KEY,
  RECEIPTS_KEY,
  RECEIPTS_AND_RECEIPT_BATCHES_PREFIX,
  CREATE_RECEIPT_KEY,
} from '../transaction/queryKeys';
import { useInvalidateReceiptsQueries } from '../transaction';
import { RECEIPT_BATCH_LIST_PHOENIX_KEY } from '../receipt-batch-phoenix/queryKeys';
import { CASH_TRANSACTION_GROUP_KEY } from '../cash-management/queryKeys';
import {
  CASH_TRANSACTION_GROUP_PHOENIX_KEY,
  CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY,
} from '../cash-management-phoenix/queryKeys';
import { PLIANT_TRANSACTIONS_KEY } from '../pliant-phoenix/queryKeys';
import type {
  CreateReceiptPhoenixParams,
  GetReceiptsParams,
  IReceiptPhoenixApi,
  ReceiptHistoryEntry,
  ReceiptIndexResponse,
  ReceiptOrBatchPhoenix,
  ReceiptPhoenix,
  ReceiptPhoenixOrderableFields,
  ReceiptsAndReceiptBatchesParams,
  ReceiptsAndReceiptBatchesResponse,
  StatusTotals,
} from './api';
import { ReceiptPhoenixApi } from './api';
import {
  GET_RECEIPT_KEY,
  RECEIPTS_AND_RECEIPT_BATCHES_PHOENIX_KEY,
  RECEIPTS_INDEX_KEY,
  REFUND_RECEIPTS_KEY,
  FAMILY_MEMBER_RECEIPTS_KEY,
} from './queryKeys';
import type { PaginatedResultSet } from '@pflegenavi/shared/utils';
import { convertPhoenixReceiptToReceiptFull } from './converters';
// Import useReceipt at the top level to avoid circular dependency issues
import { useReceipt as useReceiptLegacy } from '../transaction';

const ApiContext = createContext<IReceiptPhoenixApi | undefined>(undefined);
const {
  useApi: useReceiptsPhoenixApi,
  ApiProvider: ReceiptsPhoenixApiProvider,
} = makeApiProvider({
  name: 'ReceiptsPhoenix',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) =>
    new ReceiptPhoenixApi(tenantId, auth, apiUrl),
});

export {
  ApiContext as ReceiptsPhoenixApiContext,
  ReceiptsPhoenixApiProvider,
  useReceiptsPhoenixApi,
};
export type {
  CreateReceiptPhoenixParams,
  GetReceiptsParams,
  IReceiptPhoenixApi,
  ReceiptHistoryEntry,
  ReceiptIndexResponse,
  ReceiptOrBatchPhoenix,
  ReceiptPhoenix,
  ReceiptPhoenixApi,
  ReceiptPhoenixOrderableFields,
  ReceiptsAndReceiptBatchesParams,
  StatusTotals,
};
export { convertPhoenixReceiptToReceiptFull } from './converters';

/**
 * Hook for fetching family member receipts from Phoenix
 * Returns only Settled and Pending receipts for a specific resident
 */
export const useFamilyMemberReceiptsPhoenix = (
  residentId: string | undefined,
  tenant: string | undefined,
  options?: Omit<
    UseQueryOptions<ReceiptIndexResponse, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<ReceiptIndexResponse, unknown> => {
  const select = useCallback(
    (data: ReceiptIndexResponse): ReceiptIndexResponse => {
      return {
        ...data,
        data: data?.data?.map((receipt) => ({
          ...receipt,
          inserted_at: new Date(receipt.inserted_at),
          receipt_date: new Date(receipt.receipt_date),
          submission_date: receipt.submission_date
            ? new Date(receipt.submission_date)
            : null,
          embargo_date: receipt.embargo_date
            ? new Date(receipt.embargo_date)
            : null,
          settlement_date: receipt.settlement_date
            ? new Date(receipt.settlement_date)
            : null,
          cancellation: receipt.cancellation
            ? {
                ...receipt.cancellation,
                date: new Date(receipt.cancellation.date),
              }
            : null,
          transactions: receipt?.transactions?.map((transaction) => ({
            ...transaction,
            inserted_at: new Date(transaction.inserted_at),
          })),
        })),
      };
    },
    []
  );

  return useApiQuery(
    useReceiptsPhoenixApi,
    FAMILY_MEMBER_RECEIPTS_KEY(residentId ?? '', tenant ?? ''),
    (api) => {
      if (!residentId || !tenant) {
        throw new Error('residentId and tenant are required');
      }
      return api.getFamilyMemberReceipts({
        params: {
          tenant,
          residentId,
        },
      });
    },
    {
      ...options,
      select,
      enabled:
        options?.enabled !== undefined
          ? options.enabled
          : residentId !== undefined && tenant !== undefined,
    }
  );
};

/**
 * Unified receipt hook that can use either the legacy NestJS endpoint
 * or the new Phoenix endpoint based on the phoenixEnabled parameter.
 *
 * This enables gradual migration from NestJS to Phoenix backend without
 * causing circular dependencies.
 *
 * @param receiptId - The receipt/transaction ID
 * @param nursingHomeId - The nursing home ID (required for legacy endpoint)
 * @param phoenixEnabled - Whether to use the Phoenix endpoint
 * @param queryOptions - Optional fields to include in the response
 * @param options - React Query options
 */
export const useReceiptUnified = (
  receiptId: string,
  nursingHomeId: string | undefined,
  phoenixEnabled: boolean,
  queryOptions?: {
    optionalFields?: {
      linkedTransactionGroups?: boolean;
      receiptBatch?: boolean;
    };
  },
  options?: Omit<UseQueryOptions<ReceiptFull, unknown>, 'queryFn' | 'queryKey'>
): UseQueryResult<ReceiptFull, unknown> => {
  // Use legacy endpoint
  const legacyResult = useReceiptLegacy(
    receiptId,
    nursingHomeId,
    queryOptions,
    {
      ...options,
      enabled: !phoenixEnabled && (options?.enabled ?? true),
    }
  );

  // Map Phoenix preloads to match expected format
  const phoenixPreloads = queryOptions?.optionalFields
    ? {
        cash_list_transaction_group_link:
          queryOptions.optionalFields.linkedTransactionGroups,
        receipt_batch_entry: queryOptions.optionalFields.receiptBatch,
      }
    : undefined;

  // Use Phoenix endpoint
  const phoenixResult = useGetReceiptPhoenix(
    receiptId,
    phoenixPreloads ? { preloads: phoenixPreloads } : undefined
  );

  // Convert Phoenix result to legacy format
  const convertedPhoenixData = useCallback(() => {
    if (phoenixResult.data?.data) {
      return convertPhoenixReceiptToReceiptFull(phoenixResult.data.data);
    }
    return undefined;
  }, [phoenixResult.data]);

  // Return the appropriate result based on phoenixEnabled
  if (phoenixEnabled) {
    return {
      ...phoenixResult,
      data: convertedPhoenixData(),
      // Override enabled state based on both phoenixEnabled and original options
      isLoading: phoenixResult.isLoading && (options?.enabled ?? true),
      isFetching: phoenixResult.isFetching && (options?.enabled ?? true),
    } as unknown as UseQueryResult<ReceiptFull, unknown>;
  }

  return legacyResult;
};

export const useSubmitReceiptsPhoenix = (): UseMutationResult<
  { data: Array<{ id: string; status?: number; message?: string }> },
  unknown,
  {
    body: { receipt_ids: string[] };
    query?: { use_actually_available_balance?: boolean };
    nursingHomeId?: string;
  }
> => {
  const api = useReceiptsPhoenixApi();

  const invalidateQueries = useInvalidateReceiptsQueries();

  return useMutation<
    { data: Array<{ id: string; status?: number; message?: string }> },
    unknown,
    {
      body: { receipt_ids: string[] };
      query?: { use_actually_available_balance?: boolean };
      nursingHomeId?: string;
    },
    unknown
  >({
    mutationKey: SUBMIT_RECEIPTS_KEY,
    mutationFn: (params) => api.submit(params),
    onSettled: async (_data, _error, params) => {
      await invalidateQueries(params.nursingHomeId);
    },
  });
};

export const useCancelReceiptPhoenix = (
  nursingHomeId?: string
): UseMutationResult<
  void,
  unknown,
  {
    body: { data: { note: string } };
    params: { receipt_id: string };
  }
> => {
  const api = useReceiptsPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation<
    void,
    unknown,
    {
      body: { data: { note: string } };
      params: { receipt_id: string };
    },
    unknown
  >({
    mutationKey: REFUND_RECEIPTS_KEY,
    mutationFn: (params) => api.cancel(params),
    onSettled: async (_data, _error, params) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getReceiptKey(params.params.receipt_id),
        }),
        queryClient.invalidateQueries({
          queryKey: GET_RECEIPT_KEY(params.params.receipt_id),
        }),
        queryClient.invalidateQueries({
          queryKey: RESIDENTS_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: RESIDENTS_PHOENIX_KEY(nursingHomeId ?? ''),
        }),
        invalidateAllResidentBalance(queryClient, nursingHomeId),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_INDEX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_AND_RECEIPT_BATCHES_PHOENIX_KEY(nursingHomeId),
        }),
      ]);
    },
  });
};

export const useGetReceiptPhoenix = (
  receiptId: string,
  options?: {
    preloads?: {
      cash_list_transaction_group_link?: boolean;
      receipt_batch_entry?: boolean;
    };
  }
): UseQueryResult<{ data: ReceiptPhoenix }, unknown> => {
  const select = useCallback(
    (data: { data: ReceiptPhoenix }): { data: ReceiptPhoenix } => {
      return {
        data: {
          ...data.data,
          receipt_date: new Date(data.data.receipt_date),
          inserted_at: new Date(data.data.inserted_at),
          submission_date: data.data.submission_date
            ? new Date(data.data.submission_date)
            : null,
          embargo_date: data.data.embargo_date
            ? new Date(data.data.embargo_date)
            : null,
          settlement_date: data.data.settlement_date
            ? new Date(data.data.settlement_date)
            : null,
          cancellation: data.data.cancellation
            ? {
                ...data.data.cancellation,
                date: new Date(data.data.cancellation.date),
              }
            : null,
          transactions: data.data.transactions.map((transaction) => ({
            ...transaction,
            inserted_at: new Date(transaction.inserted_at),
          })),
        },
      };
    },
    []
  );

  return useApiQuery(
    useReceiptsPhoenixApi,
    GET_RECEIPT_KEY(receiptId, options?.preloads),
    (api) =>
      api.get({
        params: { receipt_id: receiptId },
        query: options?.preloads ? { preloads: options.preloads } : undefined,
      }),
    { select }
  );
};

export const useReceiptsPhoenix = (
  params: GetReceiptsParams,
  options?: Omit<
    UseQueryOptions<ReceiptIndexResponse, unknown>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<ReceiptIndexResponse, unknown> => {
  const select = useCallback(
    (data: ReceiptIndexResponse): ReceiptIndexResponse => {
      return {
        ...data,
        data: data?.data?.map((receipt) => ({
          ...receipt,
          inserted_at: new Date(receipt.inserted_at),
          receipt_date: new Date(receipt.receipt_date),
          submission_date: receipt.submission_date
            ? new Date(receipt.submission_date)
            : null,
          embargo_date: receipt.embargo_date
            ? new Date(receipt.embargo_date)
            : null,
          settlement_date: receipt.settlement_date
            ? new Date(receipt.settlement_date)
            : null,
          cancellation: receipt.cancellation
            ? {
                ...receipt.cancellation,
                date: new Date(receipt.cancellation.date),
              }
            : null,
          transactions: receipt?.transactions?.map((transaction) => ({
            ...transaction,
            inserted_at: new Date(transaction.inserted_at),
          })),
        })),
      };
    },
    []
  );

  return useApiQuery(
    useReceiptsPhoenixApi,
    RECEIPTS_INDEX_KEY(params),
    (api) => api.index({ params }),
    { select, ...options }
  );
};

// Wire dates arrive as ISO strings; parse them into Date objects.
const parseReceiptsAndReceiptBatchesDates = (
  data: ReceiptsAndReceiptBatchesResponse
): ReceiptsAndReceiptBatchesResponse => ({
  ...data,
  data: data.data.map((item) => ({
    ...item,
    receiptDate: new Date(item.receiptDate),
    submittedAt: item.submittedAt ? new Date(item.submittedAt) : null,
    createdAt: new Date(item.createdAt),
    minReceiptDate: item.minReceiptDate ? new Date(item.minReceiptDate) : null,
    maxReceiptDate: item.maxReceiptDate ? new Date(item.maxReceiptDate) : null,
    cancellationDate: item.cancellationDate
      ? new Date(item.cancellationDate)
      : null,
  })),
});

export const useGetReceiptsAndReceiptBatchesPhoenix = (
  params: ReceiptsAndReceiptBatchesParams,
  options?: Omit<
    UseQueryOptions<ReceiptsAndReceiptBatchesResponse, unknown>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<ReceiptsAndReceiptBatchesResponse, unknown> => {
  const select = parseReceiptsAndReceiptBatchesDates;

  return useApiQuery(
    useReceiptsPhoenixApi,
    RECEIPTS_AND_RECEIPT_BATCHES_PHOENIX_KEY(params.nursingHomeId, {
      page: params.page,
      page_size: params.page_size,
      state: params.state,
      sort: params.sort,
      sortOrder: params.sortOrder,
      submissionDate: params.submissionDate,
      createdByEmployeeIds: params.createdByEmployeeIds,
      cashTransactionGroupId: params.cashTransactionGroupId,
    }),
    (api) => api.getReceiptsAndReceiptBatches({ params }),
    { select, ...options }
  );
};

export const useGetReceiptsAndReceiptBatchesPhoenixInfiniteQuery = ({
  nursingHomeId,
  page_size = 10,
  ...params
}: Omit<
  ReceiptsAndReceiptBatchesParams,
  'page' | 'nursingHomeId' | 'page_size'
> & {
  page_size?: number;
  nursingHomeId: string | undefined;
}): UseInfiniteQueryResult<
  InfiniteData<PaginatedResultSet<ReceiptOrBatchPhoenix>>,
  unknown
> => {
  const api = useReceiptsPhoenixApi();

  const queryKey = RECEIPTS_AND_RECEIPT_BATCHES_PHOENIX_KEY(nursingHomeId, {
    page_size,
    ...params,
  });

  return useInfiniteQuery({
    queryKey,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      if (!nursingHomeId) {
        throw new Error('nursingHomeId is required');
      }
      const result = await api.getReceiptsAndReceiptBatches({
        params: {
          ...params,
          page: pageParam as number,
          page_size,
          nursingHomeId,
        },
      });

      return parseReceiptsAndReceiptBatchesDates(result);
    },
    getNextPageParam: (lastPage: ReceiptsAndReceiptBatchesResponse) => {
      if (lastPage.meta.currentPage < lastPage.meta.totalPages) {
        return lastPage.meta.currentPage + 1;
      }
      return undefined;
    },
    enabled: !!nursingHomeId,
  });
};

export const useTransitionToAwaitingSettlement = (
  nursingHomeId?: string
): UseMutationResult<
  { data: ReceiptPhoenix },
  unknown,
  { receiptId: string }
> => {
  const api = useReceiptsPhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<
    { data: ReceiptPhoenix },
    unknown,
    { receiptId: string },
    unknown
  >({
    mutationFn: ({ receiptId }) =>
      api.transitionToAwaitingSettlement({
        params: { receipt_id: receiptId },
      }),
    onSettled: async (_data, _error, { receiptId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getReceiptKey(receiptId),
        }),
        queryClient.invalidateQueries({
          queryKey: GET_RECEIPT_KEY(receiptId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_INDEX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_AND_RECEIPT_BATCHES_PHOENIX_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: RESIDENTS_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: RESIDENTS_PHOENIX_KEY(nursingHomeId ?? ''),
        }),
      ]);
    },
  });
};

export const useRevertAwaitingSettlement = (
  nursingHomeId?: string
): UseMutationResult<
  { data: ReceiptPhoenix },
  unknown,
  { receiptId: string }
> => {
  const api = useReceiptsPhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<
    { data: ReceiptPhoenix },
    unknown,
    { receiptId: string },
    unknown
  >({
    mutationFn: ({ receiptId }) =>
      api.revertAwaitingSettlement({
        params: { receipt_id: receiptId },
      }),
    onSettled: async (_data, _error, { receiptId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getReceiptKey(receiptId),
        }),
        queryClient.invalidateQueries({
          queryKey: GET_RECEIPT_KEY(receiptId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_INDEX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_AND_RECEIPT_BATCHES_PHOENIX_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: RESIDENTS_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: RESIDENTS_PHOENIX_KEY(nursingHomeId ?? ''),
        }),
      ]);
    },
  });
};

export const useSettleReceipt = (
  nursingHomeId?: string
): UseMutationResult<
  { data: ReceiptPhoenix },
  unknown,
  {
    receiptId: string;
    cashTransactionGroupId?: string;
    notes?: string;
  }
> => {
  const api = useReceiptsPhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<
    { data: ReceiptPhoenix },
    unknown,
    {
      receiptId: string;
      cashTransactionGroupId?: string;
      notes?: string;
    },
    unknown
  >({
    mutationFn: ({ receiptId, cashTransactionGroupId, notes }) =>
      api.settle({
        params: { receipt_id: receiptId },
        body: {
          ...(cashTransactionGroupId
            ? { cash_transaction_group_id: cashTransactionGroupId }
            : {}),
          notes,
        },
      }),
    onSettled: async (_data, _error, { receiptId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getReceiptKey(receiptId),
        }),
        queryClient.invalidateQueries({
          queryKey: GET_RECEIPT_KEY(receiptId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_INDEX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_AND_RECEIPT_BATCHES_PHOENIX_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: RESIDENTS_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: RESIDENTS_PHOENIX_KEY(nursingHomeId ?? ''),
        }),
        invalidateAllResidentBalance(queryClient, nursingHomeId),
      ]);
    },
  });
};

/**
 * Hook for creating receipts using the Phoenix endpoint
 * Provides atomic creation with optional cash transaction group linking
 */
export const useCreateReceiptPhoenix = (): UseMutationResult<
  ReceiptFull,
  unknown,
  CreateReceiptPhoenixParams
> => {
  const api = useReceiptsPhoenixApi();
  const queryClient = useQueryClient();

  return useMutation<ReceiptFull, unknown, CreateReceiptPhoenixParams, unknown>(
    {
      mutationKey: CREATE_RECEIPT_KEY,
      mutationFn: async (params) => {
        const response = await api.create({ body: params });
        // Convert Phoenix response to ReceiptFull format for compatibility
        return convertPhoenixReceiptToReceiptFull(response.data);
      },
      onSuccess: async (data, params) => {
        // Invalidate cash-related queries if a cash link was created
        if (
          params.cash_list_transaction_group_link
            ?.cash_list_transaction_group_id
        ) {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: CASH_TRANSACTION_GROUP_KEY(
                params.cash_list_transaction_group_link
                  .cash_list_transaction_group_id
              ),
            }),
            queryClient.invalidateQueries({
              queryKey: CASH_TRANSACTION_GROUP_PHOENIX_KEY(
                params.cash_list_transaction_group_link
                  .cash_list_transaction_group_id
              ),
            }),
          ]);
        }

        // Invalidate receipt-related queries (matching legacy hook behavior)
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: RECEIPTS_KEY }),
          queryClient.invalidateQueries({
            queryKey: RECEIPTS_AND_RECEIPT_BATCHES_PREFIX,
          }),
          queryClient.invalidateQueries({
            queryKey: RECEIPTS_INDEX_KEY(),
          }),
          queryClient.invalidateQueries({
            queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
          }),
          invalidatePendingReceiptCount(queryClient),
          // Invalidate queries used by useNursingHomeReceipts hook
          queryClient.invalidateQueries({
            queryKey: PLIANT_TRANSACTIONS_KEY(),
          }),
          queryClient.invalidateQueries({
            queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
          }),
        ]);
      },
    }
  );
};
